import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Upload, FileText, Trash2, ArrowRight, CheckCircle2, AlertCircle,
  Loader2, X as XIcon, FileSpreadsheet, ChevronDown, RefreshCw,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SYSTEM_FIELDS = [
  { id: 'date', label: 'Date', required: true },
  { id: 'description', label: 'Description', required: true },
  { id: 'amount', label: 'Amount', required: true },
  { id: 'type', label: 'Type (Debit/Credit)', required: false },
];

const fmtUSD = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

export const CrmBankImports = () => {
  const navigate = useNavigate();
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchImports = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/accounting/bank-imports`, { withCredentials: true });
      setImports(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bank imports');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchImports(); }, [fetchImports]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await axios.delete(
        `${API_URL}/api/admin/accounting/bank-imports/${deleteTarget.import_id}`,
        { withCredentials: true },
      );
      toast.success(`Import deleted · ${r.data?.cascade_transactions || 0} transactions removed`);
      setDeleteTarget(null);
      fetchImports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setDeleting(false); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="bank-imports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate('/crm/accounting')}
              className="text-xs text-[#6B6B7A] hover:text-[#FF3B6B]"
              data-testid="back-to-accounting"
            >
              ← Accounting
            </button>
          </div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Bank Imports
          </h1>
          <p className="text-sm text-[#6B6B7A] mt-1">
            Upload bank statements (CSV), classify transactions as Income or Expense, and reconcile against bookings.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchImports}
            className="rounded-full border-[#E8E5DF]"
            data-testid="bank-imports-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button
            onClick={() => setWizardOpen(true)}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            data-testid="new-bank-import-btn"
          >
            <Upload className="w-4 h-4 mr-1" /> New Import
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <Table>
          <TableHeader className="bg-[#FFFDF8]">
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead>Bank</TableHead>
              <TableHead className="text-right">Rows</TableHead>
              <TableHead className="text-right">Classified</TableHead>
              <TableHead className="text-right">Skipped</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#6B6B7A]">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
              </TableCell></TableRow>
            ) : imports.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12">
                <FileSpreadsheet className="w-10 h-10 text-[#E8E5DF] mx-auto mb-2" />
                <p className="text-sm text-[#6B6B7A]">No bank imports yet</p>
                <Button
                  onClick={() => setWizardOpen(true)}
                  className="mt-3 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
                  data-testid="bank-imports-empty-cta"
                >
                  <Upload className="w-4 h-4 mr-1" /> Upload your first CSV
                </Button>
              </TableCell></TableRow>
            ) : (
              imports.map((imp) => {
                const pct = imp.row_count > 0 ? Math.round((imp.classified_count / imp.row_count) * 100) : 0;
                return (
                  <TableRow
                    key={imp.import_id}
                    className="cursor-pointer hover:bg-[#FFFDF8]"
                    onClick={() => navigate(`/crm/accounting/bank-imports/${imp.import_id}`)}
                    data-testid={`bank-import-row-${imp.import_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#FF3B6B]" />
                        <div>
                          <p className="font-medium text-sm text-[#1E1E24]">{imp.filename}</p>
                          {imp.notes && <p className="text-[11px] text-[#6B6B7A] line-clamp-1">{imp.notes}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[#6B6B7A]">{imp.bank_name || '—'}</TableCell>
                    <TableCell className="text-right font-semibold">{imp.row_count}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-[#F0EDE6] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#06D6A0] transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs font-mono">{pct}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-xs text-[#6B6B7A]">
                      {imp.skipped_invalid > 0 && <span title="Invalid rows">{imp.skipped_invalid} bad</span>}
                      {imp.skipped_invalid > 0 && imp.skipped_duplicate > 0 && ' · '}
                      {imp.skipped_duplicate > 0 && <span className="text-[#B8860B]" title="Duplicates skipped">{imp.skipped_duplicate} dup</span>}
                      {!imp.skipped_invalid && !imp.skipped_duplicate && '—'}
                    </TableCell>
                    <TableCell className="text-xs text-[#6B6B7A]">
                      <div>{fmtDate(imp.uploaded_at)}</div>
                      <div className="text-[10px]">by {imp.uploaded_by_name || '—'}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm" variant="ghost"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(imp); }}
                        className="text-[#D1344D] hover:bg-[#FFE4E1] h-8 w-8 p-0"
                        title="Delete import"
                        data-testid={`bank-import-delete-${imp.import_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <ImportWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onImported={(importId) => {
          setWizardOpen(false);
          fetchImports();
          // Jump straight into classification
          navigate(`/crm/accounting/bank-imports/${importId}`);
        }}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="bank-import-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete this import?</DialogTitle>
            <DialogDescription className="text-xs">
              {deleteTarget && (<>
                <strong>{deleteTarget.filename}</strong> — {deleteTarget.row_count} transactions will be removed
                from your reports. This cannot be undone from the UI.
              </>)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="rounded-full bg-[#D1344D] hover:bg-[#b9293f] text-white"
              data-testid="bank-import-delete-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─────────── Upload wizard (drag-drop → preview → confirm) ───────────
const ImportWizard = ({ open, onOpenChange, onImported }) => {
  const [step, setStep] = useState('upload'); // 'upload' | 'mapping' | 'submitting'
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({ date: null, description: null, amount: null, type: null, skip_header: true });
  const [defaultDirection, setDefaultDirection] = useState('');
  const [bankName, setBankName] = useState('');
  const [notes, setNotes] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // reset on close
      setStep('upload');
      setPreview(null);
      setMapping({ date: null, description: null, amount: null, type: null, skip_header: true });
      setDefaultDirection('');
      setBankName('');
      setNotes('');
      setSubmitting(false);
    }
  }, [open]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.(csv|txt)$/i.test(file.name)) {
      toast.error('Please upload a .csv file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File exceeds 5 MB limit');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API_URL}/api/admin/accounting/bank-imports/preview`,
        fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setPreview(r.data);
      setMapping({
        date: r.data.suggested_mapping.date,
        description: r.data.suggested_mapping.description,
        amount: r.data.suggested_mapping.amount,
        type: r.data.suggested_mapping.type,
        skip_header: true,
      });
      setStep('mapping');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setSubmitting(false); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const handleConfirm = async () => {
    if (mapping.date === null || mapping.description === null || mapping.amount === null) {
      toast.error('Please map Date, Description and Amount columns');
      return;
    }
    if (mapping.type === null && !defaultDirection) {
      toast.error('Either map a Type column OR choose a default direction (all debits / all credits)');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        filename: preview.filename,
        rows: preview.rows,
        mapping: {
          date: mapping.date,
          description: mapping.description,
          amount: mapping.amount,
          type: mapping.type,
          skip_header: mapping.skip_header,
          default_direction: defaultDirection || null,
        },
        bank_name: bankName,
        notes: notes,
      };
      const r = await axios.post(
        `${API_URL}/api/admin/accounting/bank-imports/confirm`,
        payload, { withCredentials: true },
      );
      const imp = r.data?.import;
      const skipped = imp?.skipped_duplicate || 0;
      const invalid = imp?.skipped_invalid || 0;
      let msg = `Imported ${r.data.transactions_created} transactions`;
      if (skipped > 0) msg += ` · ${skipped} duplicate${skipped === 1 ? '' : 's'} skipped`;
      if (invalid > 0) msg += ` · ${invalid} unreadable row${invalid === 1 ? '' : 's'} skipped`;
      toast.success(msg);
      onImported?.(imp.import_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setSubmitting(false); }
  };

  const renderUploadStep = () => (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-[#FF3B6B] bg-[#FFF5F8]' : 'border-[#E8E5DF] hover:border-[#FF3B6B] hover:bg-[#FFFDF8]'
        }`}
        data-testid="bank-upload-dropzone"
      >
        <Upload className="w-12 h-12 text-[#FF3B6B] mx-auto mb-3" />
        <p className="font-semibold text-[#1E1E24]">Drop your CSV file here</p>
        <p className="text-sm text-[#6B6B7A] mt-1">or click to browse — max 5 MB / 50,000 rows</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt,text/csv"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="bank-upload-file-input"
        />
      </div>
      <div className="bg-[#FFF7EB] border border-[#F5C15A] rounded-xl p-3 text-xs text-[#8A5A1F] flex gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          Your CSV should have a header row with column names like <em>Date, Description, Amount, Type</em>.
          Rows already imported (same date + amount + description) will be skipped automatically.
        </span>
      </div>
    </div>
  );

  const renderMappingStep = () => {
    if (!preview) return null;
    const headers = preview.headers || [];
    const setField = (field, idx) => setMapping((m) => ({ ...m, [field]: idx === '__none__' ? null : Number(idx) }));
    return (
      <div className="space-y-4" data-testid="bank-mapping-step">
        <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3">
          <p className="text-xs text-[#6B6B7A]">
            <strong>{preview.filename}</strong> · {preview.total_rows} rows · {headers.length} columns
          </p>
        </div>

        {/* Preview table — first 5 data rows */}
        <div className="border border-[#E8E5DF] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-[#FFFDF8]">
                <TableRow>
                  {headers.map((h, i) => (
                    <TableHead key={i} className="text-xs whitespace-nowrap">
                      <div className="font-bold">{h || `Col ${i + 1}`}</div>
                      <div className="text-[10px] font-normal text-[#6B6B7A]">Column {i + 1}</div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(preview.preview_rows || []).map((row, ri) => (
                  <TableRow key={ri}>
                    {row.map((cell, ci) => (
                      <TableCell key={ci} className="text-xs truncate max-w-[200px]">{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Mapping selects */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SYSTEM_FIELDS.map((f) => (
            <div key={f.id}>
              <Label className="text-xs">
                {f.label}{f.required && <span className="text-[#D1344D] ml-1">*</span>}
              </Label>
              <Select
                value={mapping[f.id] === null ? '__none__' : String(mapping[f.id])}
                onValueChange={(v) => setField(f.id, v)}
              >
                <SelectTrigger className="mt-1 bg-white" data-testid={`bank-map-${f.id}`}>
                  <SelectValue placeholder={f.required ? 'Pick a column' : '(none / not in CSV)'} />
                </SelectTrigger>
                <SelectContent>
                  {!f.required && <SelectItem value="__none__">(none — not in CSV)</SelectItem>}
                  {headers.map((h, i) => (
                    <SelectItem key={i} value={String(i)}>{h || `Column ${i + 1}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>

        {/* Optional defaults */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">If no Type column — all rows are:</Label>
            <Select value={defaultDirection || '__none__'} onValueChange={(v) => setDefaultDirection(v === '__none__' ? '' : v)}>
              <SelectTrigger className="mt-1 bg-white" data-testid="bank-default-direction">
                <SelectValue placeholder="Auto (use amount sign)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Auto — read amount sign</SelectItem>
                <SelectItem value="credit">All Credits (Income / Inflow)</SelectItem>
                <SelectItem value="debit">All Debits (Expense / Outflow)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-[#1E1E24] cursor-pointer">
              <input
                type="checkbox"
                checked={mapping.skip_header}
                onChange={(e) => setMapping((m) => ({ ...m, skip_header: e.target.checked }))}
                className="rounded border-[#E8E5DF]"
                data-testid="bank-skip-header"
              />
              First row is a header — skip it
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-[#E8E5DF]">
          <div>
            <Label className="text-xs">Bank name (optional)</Label>
            <Input
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="e.g. TD Business, RBC, BMO"
              className="mt-1"
              data-testid="bank-import-bank-name"
            />
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. January 2026 statement"
              className="mt-1"
              data-testid="bank-import-notes"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bank-import-wizard">
        <DialogHeader>
          <DialogTitle>
            {step === 'upload' && 'Upload bank statement'}
            {step === 'mapping' && 'Map columns to system fields'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {step === 'upload' && 'CSV from any bank — we auto-suggest column mapping in the next step.'}
            {step === 'mapping' && 'We auto-detected your columns. Tweak if needed, then import.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && renderUploadStep()}
        {step === 'mapping' && renderMappingStep()}

        {submitting && step === 'upload' && (
          <div className="flex items-center gap-2 text-sm text-[#6B6B7A]">
            <Loader2 className="w-4 h-4 animate-spin" /> Parsing CSV…
          </div>
        )}

        <DialogFooter className="gap-2 mt-3 border-t border-[#E8E5DF] pt-3">
          {step === 'mapping' && (
            <Button
              variant="ghost"
              onClick={() => { setStep('upload'); setPreview(null); }}
              disabled={submitting}
              data-testid="bank-wizard-back"
            >
              ← Re-upload
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          {step === 'mapping' && (
            <Button
              onClick={handleConfirm}
              disabled={submitting}
              className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
              data-testid="bank-wizard-import"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Import {preview ? `${(mapping.skip_header ? (preview.rows.length - 1) : preview.rows.length)} rows` : ''}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CrmBankImports;
